/**
 * useTableSort — Reusable table sorting hook + `<SortHeader>` component.
 *
 * One place for sorting state management, used across user-side and admin
 * tables. Keeps sort state (`{field, dir}`) and a stable, type-aware
 * comparator.
 *
 * Sorting cycle on clicking a header:
 *   idle → asc → desc → idle (back to the provided initial/default order)
 *
 * Type-aware comparison:
 *   • numbers / numeric-looking strings → numeric comparison
 *   • Date objects / ISO-8601 strings    → chronological
 *   • strings                            → case-insensitive localeCompare
 *   • null / undefined / ''              → always sorted LAST regardless of dir
 *
 * Rendering header cells:
 *   <SortHeader {...sort} field="ticker" thClass="admin-th">Ticker</SortHeader>
 *   where `sort = useTableSort(data)`.
 */
import { useState, useMemo, useCallback } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const ASC = 1;
const DESC = -1;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ].+)?$/;


function _coerce(value, accessor) {
  if (accessor && typeof accessor === 'function') return accessor(value);
  return value;
}


/** Extract a comparable primitive from a cell value. */
function _normalise(v) {
  if (v === null || v === undefined) return { nully: true };
  if (v instanceof Date) return { kind: 'num', num: v.getTime() };
  if (typeof v === 'number') {
    return Number.isNaN(v) ? { nully: true } : { kind: 'num', num: v };
  }
  if (typeof v === 'boolean') return { kind: 'num', num: v ? 1 : 0 };

  const str = String(v).trim();
  if (str === '') return { nully: true };

  // Percentage? e.g. "12,5%" or "12.5 %" → strip and parse.
  let candidate = str;
  let isPct = false;
  if (candidate.endsWith('%')) {
    candidate = candidate.slice(0, -1).trim();
    isPct = true;
  }
  // European decimals: swap comma for dot IF there's no thousand-sep dot.
  const hasThousandDot = /\.\d{3}(?:[^\d]|$)/.test(candidate);
  if (!hasThousandDot && candidate.includes(',')) {
    candidate = candidate.replace(/\./g, '').replace(',', '.');
  }
  const num = Number(candidate);
  if (!Number.isNaN(num) && /^-?[\d.]+$/.test(candidate.replace(/^\-/, ''))) {
    return { kind: 'num', num, isPct };
  }

  if (ISO_DATE_RE.test(str)) {
    const d = Date.parse(str);
    if (!Number.isNaN(d)) return { kind: 'num', num: d };
  }

  return { kind: 'str', str: str.toLowerCase() };
}


/**
 * Stable comparator. Nullish always goes to the end regardless of dir.
 * Returns a negative/zero/positive number.
 */
export function compareCells(a, b, dir = ASC) {
  const na = _normalise(a);
  const nb = _normalise(b);
  if (na.nully && nb.nully) return 0;
  if (na.nully) return 1;
  if (nb.nully) return -1;
  if (na.kind === 'num' && nb.kind === 'num') {
    return (na.num - nb.num) * dir;
  }
  // Mixed number + string: numbers come first in ascending order.
  if (na.kind === 'num') return -dir;
  if (nb.kind === 'num') return dir;
  return na.str.localeCompare(nb.str, undefined, { numeric: true }) * dir;
}


function _getFieldValue(row, field, accessor) {
  if (accessor) return _coerce(row, accessor);
  return row ? row[field] : undefined;
}


/**
 * React hook: manages sort state + returns sorted rows.
 *
 * @param {Array} rows - the data to sort (kept untouched — returns a new array)
 * @param {object} [options]
 * @param {object} [options.accessors] - `{ [field]: (row) => cellValue }`
 *        override for fields that aren't just `row[field]`.
 * @param {{field: string, dir: 1|-1}} [options.initial] - initial sort
 *        (also acts as the "idle/default" ordering restored on the 3rd click)
 * @param {string[]} [options.disabledFields] - fields that can't be sorted
 */
export function useTableSort(rows, options = {}) {
  const { accessors, initial = null, disabledFields = [] } = options;
  const [sort, setSort] = useState(initial);  // `null` or `{field, dir}`

  const toggle = useCallback((field) => {
    if (disabledFields.includes(field)) return;
    setSort((prev) => {
      if (!prev || prev.field !== field) return { field, dir: ASC };
      if (prev.dir === ASC) return { field, dir: DESC };
      if (initial && initial.field === field) return initial;
      return null;
    });
  }, [disabledFields, initial]);

  const reset = useCallback(() => setSort(initial), [initial]);

  const sorted = useMemo(() => {
    const data = rows || [];
    const active = sort || initial;
    if (!active || !active.field) return data.slice();
    const { field, dir } = active;
    const accessor = accessors && accessors[field];
    const copy = data.slice();
    copy.sort((a, b) => compareCells(
      _getFieldValue(a, field, accessor),
      _getFieldValue(b, field, accessor),
      dir,
    ));
    return copy;
  }, [rows, sort, initial, accessors]);

  return {
    sorted,
    sortField: sort?.field ?? null,
    sortDir: sort?.dir ?? 0,      // 0 means idle/default
    toggle,
    reset,
  };
}


/**
 * Accessible sortable table header cell.
 *
 *   <SortHeader
 *     field="ticker" sortField={s.sortField} sortDir={s.sortDir}
 *     onSort={s.toggle} thClass="admin-th">
 *     Ticker
 *   </SortHeader>
 */
export function SortHeader({
  field, sortField, sortDir, onSort, thClass = 'admin-th',
  thStyle, align = 'left', disabled = false, children,
}) {
  const active = sortField === field && sortDir !== 0;
  let ariaSort = 'none';
  if (active) ariaSort = sortDir === ASC ? 'ascending' : 'descending';

  let justify = '';
  if (align === 'right') justify = 'justify-end';
  else if (align === 'center') justify = 'justify-center';

  let Icon = ArrowUpDown;
  if (active && sortDir === ASC) Icon = ArrowUp;
  else if (active && sortDir === DESC) Icon = ArrowDown;

  const thClassName = disabled
    ? thClass
    : `${thClass} cursor-pointer select-none group`;
  const iconClass = active
    ? 'opacity-100 text-blue-400'
    : 'opacity-30 group-hover:opacity-70';

  return (
    <th
      className={thClassName}
      style={thStyle}
      aria-sort={ariaSort}
      data-testid={disabled ? undefined : `sort-th-${field}`}
      onClick={disabled ? undefined : () => onSort(field)}
    >
      <span className={`inline-flex items-center gap-1 ${justify}`}>
        {children}
        {!disabled && <Icon className={`w-3 h-3 transition-opacity ${iconClass}`} />}
      </span>
    </th>
  );
}
